import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowCounterClockwise, Camera, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhotoGallery } from "@/components/PhotoGallery";
import { api, formatApiErrorDetail, photoHref } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { shrinkAll } from "@/lib/shrinkImage";
import { hasPendingHd, usePendingHdRefresh } from "@/lib/photoHd";

export const JobPhotos = ({ jobId, job, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [caption, setCaption] = useState("");
  const [showTrash, setShowTrash] = useState(false);
  const { can } = usePermissions();
  const canDelete = can("delete:photos");
  const photos = (job?.photos || []).filter((p) => !p.is_deleted);

  const refresh = useCallback(
    () =>
      api
        .get(`/jobs/${jobId}`)
        .then(({ data }) => onChange(data))
        .catch(() => {}),
    [jobId, onChange],
  );
  usePendingHdRefresh(hasPendingHd(photos), refresh);

  const revertHd = async (photo) => {
    try {
      const { data } = await api.post("/photos/revert-hd", {
        collection: "jobs",
        doc_id: jobId,
        photo_id: photo.id,
      });
      onChange(data);
      toast.success("Original photo restored");
    } catch {
      toast.error("Could not restore the original");
    }
  };

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      (await shrinkAll(picked)).forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/jobs/${jobId}/photos`, fd);
      onChange(data);
      toast.success(
        picked.length === 1
          ? "Photo attached"
          : `${picked.length} photos attached`,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Photo upload failed",
      );
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const saveCaption = async (photoId, caption) => {
    const { data } = await api.patch(`/jobs/${jobId}/photos/${photoId}`, {
      caption,
    });
    onChange(data);
    setEditing(null);
    toast.success("Description saved");
  };

  const remove = async (photoId) => {
    const { data } = await api.delete(`/jobs/${jobId}/photos/${photoId}`);
    onChange(data);
    toast.success("Photo moved to trash · restore within 30 days");
  };

  const restore = async (photoId) => {
    try {
      const { data } = await api.post(`/jobs/${jobId}/photos/${photoId}/restore`);
      onChange(data);
      toast.success("Photo restored");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not restore");
    }
  };
  const DAY = 86400000;
  const trash = (job?.photos || []).filter(
    (p) => p.is_deleted && (!p.deleted_at || Date.now() - new Date(p.deleted_at).getTime() < 30 * DAY),
  );
  const daysLeft = (p) =>
    p.deleted_at ? Math.max(1, 30 - Math.floor((Date.now() - new Date(p.deleted_at).getTime()) / DAY)) : 30;

  return (
    <div
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid="job-photos-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs uppercase tracking-widest text-zinc-500">
          Job Photos ({photos.length})
        </div>
        <Button
          data-testid="job-add-photos-button"
          variant="outline"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="h-10 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
        >
          <Camera size={15} weight="bold" className="mr-2" />
          {busy ? "Uploading…" : "Add Photos"}
        </Button>
        <input
          ref={inputRef}
          data-testid="job-photo-input"
          type="file"
          accept="image/*"
          multiple
          onChange={upload}
          className="hidden"
        />
      </div>
      <PhotoGallery
        photos={photos}
        onRemove={canDelete ? remove : undefined}
        onRevertHd={revertHd}
        testPrefix="job-photo"
      />
      {photos.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="job-photo-captions">
          {photos.map((ph) => (
            <div
              key={ph.id}
              className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-2"
            >
              {editing === ph.id ? (
                <>
                  <Input
                    data-testid={`job-photo-caption-input-${ph.id}`}
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="Describe this photo…"
                    className="h-10 border-zinc-800 bg-zinc-900"
                  />
                  <Button
                    data-testid={`job-photo-caption-save-${ph.id}`}
                    onClick={() => saveCaption(ph.id, caption)}
                    className="h-10 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-sm text-zinc-300">
                    {ph.caption || "No description"}
                  </span>
                  <button
                    data-testid={`job-photo-caption-edit-${ph.id}`}
                    onClick={() => {
                      setEditing(ph.id);
                      setCaption(ph.caption || "");
                    }}
                    className="shrink-0 text-[10px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    {ph.caption ? "Edit" : "Add description"}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {canDelete && trash.length > 0 && (
        <div className="mt-4 rounded-md border border-dashed border-zinc-800 p-3" data-testid="job-photo-trash">
          <button
            type="button"
            data-testid="job-photo-trash-toggle"
            onClick={() => setShowTrash((v) => !v)}
            className="flex w-full items-center justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
          >
            <span className="inline-flex items-center gap-1.5">
              <Trash size={12} weight="bold" /> Trash · {trash.length} photo{trash.length === 1 ? "" : "s"}
            </span>
            <span>{showTrash ? "Hide" : "Show"}</span>
          </button>
          {showTrash && (
            <div className="mt-3 flex flex-wrap gap-3">
              {trash.map((ph) => (
                <div key={ph.id} className="w-24 text-center" data-testid={`job-photo-trash-${ph.id}`}>
                  <img
                    src={photoHref(ph.thumb_path || ph.storage_path)}
                    alt={ph.caption || "Deleted photo"}
                    className="h-24 w-24 rounded-md border border-zinc-800 object-cover opacity-60"
                  />
                  <div className="mt-1 text-[10px] text-zinc-500">{daysLeft(ph)}d left</div>
                  <button
                    type="button"
                    data-testid={`job-photo-restore-${ph.id}`}
                    onClick={() => restore(ph.id)}
                    className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300 hover:underline"
                  >
                    <ArrowCounterClockwise size={11} weight="bold" /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
